import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Star } from 'lucide-react';
import { pricingData } from '../data/mockData';

const Pricing = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const categories = ['all', ...pricingData.map(cat => cat.category)];
  
  const filteredCategories = selectedCategory === 'all' 
    ? pricingData 
    : pricingData.filter(cat => cat.category === selectedCategory);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--warm-gradient)' }}>
        <div className="container text-center">
          <h1 className="mb-6 fade-in" style={{ color: 'var(--charcoal)' }}>
            Transparent Pricing
          </h1>
          <p className="text-xl max-w-3xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
            Luxury beauty treatments with clear, honest pricing. 
            Invest in yourself with our premium services designed to enhance your natural beauty.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8" style={{ backgroundColor: 'var(--pure-white)' }}>
        <div className="container">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${
                  selectedCategory === category
                    ? 'text-white shadow-lg' 
                    : 'bg-white text-charcoal border-2 hover:bg-warm-beige'
                }`}
                style={{
                  backgroundColor: selectedCategory === category ? 'var(--champagne-gold)' : 'var(--pure-white)',
                  borderColor: 'var(--champagne-gold)',
                  color: selectedCategory === category ? 'var(--pure-white)' : 'var(--charcoal)'
                }}
              >
                {category === 'all' ? 'All Services' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="section">
        <div className="container">
          <div className="space-y-12">
            {filteredCategories.map((category, categoryIndex) => (
              <div key={category.category} className={`fade-in-delay-${categoryIndex + 1}`}>
                <div className="text-center mb-8">
                  <h2 className="mb-4" style={{ color: 'var(--charcoal)' }}>
                    {category.category}
                  </h2>
                  <div className="w-24 h-1 mx-auto rounded-full" style={{ backgroundColor: 'var(--champagne-gold)' }}></div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="grid grid-cols-1 divide-y" style={{ borderColor: 'var(--warm-beige)' }}>
                    {category.services.map((service, serviceIndex) => (
                      <div 
                        key={serviceIndex}
                        className="flex flex-col sm:flex-row items-center justify-between p-6 hover:bg-warm-beige transition-colors duration-300"
                        style={{ backgroundColor: serviceIndex % 2 === 0 ? 'var(--pure-white)' : 'var(--warm-beige)' }}
                      >
                        <div className="flex-1 mb-4 sm:mb-0">
                          <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--charcoal)' }}>
                            {service.name}
                          </h3>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <Check className="w-4 h-4" style={{ color: 'var(--champagne-gold)' }} />
                              <span className="text-sm text-gray-600">Professional consultation</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Check className="w-4 h-4" style={{ color: 'var(--champagne-gold)' }} />
                              <span className="text-sm text-gray-600">Premium products</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Check className="w-4 h-4" style={{ color: 'var(--champagne-gold)' }} />
                              <span className="text-sm text-gray-600">Aftercare included</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-6">
                          <div className="text-right">
                            <div className="text-3xl font-bold" style={{ color: 'var(--champagne-gold)' }}>
                              €{service.price}
                            </div>
                            <div className="text-sm text-gray-500">per session</div>
                          </div>
                          
                          <Link 
                            to="/contact"
                            className="btn-primary whitespace-nowrap"
                          >
                            <span>Book Now</span>
                            <ArrowRight size={16} />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Package Deals Section */}
      <section className="section" style={{ background: 'var(--blush-gradient)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="mb-4 fade-in" style={{ color: 'var(--charcoal)' }}>
              Exclusive Packages
            </h2>
            <p className="text-lg max-w-2xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
              Save more with our curated treatment packages designed for comprehensive beauty care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Signature Package */}
            <div className="bg-white rounded-2xl p-8 shadow-lg fade-in-delay-1 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                POPULAR
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--charcoal)' }}>
                Signature Glow
              </h3>
              <p className="text-gray-600 mb-6">
                Perfect introduction to our luxury treatments
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Luxury Facial Treatment</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Professional Hair Styling</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Relaxation Massage</span>
                </div>
              </div>

              <div className="border-t pt-6" style={{ borderColor: 'var(--warm-beige)' }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 line-through">€290</span>
                  <span className="text-3xl font-bold" style={{ color: 'var(--champagne-gold)' }}>€250</span>
                </div>
                <Link to="/contact" className="btn-primary w-full text-center">
                  Book Package
                </Link>
              </div>
            </div>

            {/* Premium Package */}
            <div className="bg-white rounded-2xl p-8 shadow-lg fade-in-delay-2">
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--charcoal)' }}>
                Premium Transformation
              </h3>
              <p className="text-gray-600 mb-6">
                Complete beauty transformation experience
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Anti-Aging Treatment</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Hair Color & Styling</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Body Contouring</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Luxury Aftercare Kit</span>
                </div>
              </div>

              <div className="border-t pt-6" style={{ borderColor: 'var(--warm-beige)' }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 line-through">€650</span>
                  <span className="text-3xl font-bold" style={{ color: 'var(--champagne-gold)' }}>€550</span>
                </div>
                <Link to="/contact" className="btn-secondary w-full text-center">
                  Book Package
                </Link>
              </div>
            </div>

            {/* VIP Package */}
            <div className="bg-white rounded-2xl p-8 shadow-lg fade-in-delay-3">
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--charcoal)' }}>
                VIP Experience
              </h3>
              <p className="text-gray-600 mb-6">
                Ultimate luxury with exclusive perks
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>All Premium Services</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Injectable Treatments</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Private Suite Access</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                  <span>Champagne & Refreshments</span>
                </div>
              </div>

              <div className="border-t pt-6" style={{ borderColor: 'var(--warm-beige)' }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 line-through">€950</span>
                  <span className="text-3xl font-bold" style={{ color: 'var(--champagne-gold)' }}>€800</span>
                </div>
                <Link to="/contact" className="btn-secondary w-full text-center">
                  Book VIP
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-charcoal text-white" style={{ backgroundColor: 'var(--charcoal)' }}>
        <div className="container text-center">
          <h2 className="mb-6 text-white fade-in">
            Questions About Pricing?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto fade-in-delay-1">
            Our beauty consultants are here to help you choose the perfect treatment plan for your needs and budget.
          </p>
          <Link to="/contact" className="btn-primary fade-in-delay-2">
            Schedule Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;